// Preauthorization Writer — new request form scoped to a Case.
//
// Implements: SPEC §4.2.3 (Preauthorization Writer UI) + SPEC-ADDENDUM §A7
// Phase 4 "Frontend" — goal / authorship / workflow selectors, Provider gated
// by the provisioned clinical_authority claim, insurance-profile picker, the
// Appeal-mode denial-letter requirement, and the supported-carrier/jurisdiction
// banner. On submit it creates the preauth request then generates the draft,
// and navigates to the viewer.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, cases, documents, insuranceProfiles, preauth } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { useAuthStore } from "../lib/store";
import { pushToast } from "../components/Toast";

const GOALS: { value: string; label: string }[] = [
  { value: "medication", label: "Medication" },
  { value: "procedure", label: "Procedure" },
  { value: "surgery", label: "Surgery" },
];

const WORKFLOWS: { value: string; label: string }[] = [
  { value: "request", label: "Request (no denial yet)" },
  { value: "appeal", label: "Appeal (denial already issued)" },
];

const AUTHORSHIPS: { value: string; label: string }[] = [
  { value: "patient", label: "Patient" },
  { value: "helper", label: "Helper" },
  { value: "provider", label: "Provider" },
];

interface VerifierFailure {
  offending_citations: string[];
  offending_document_ids: number[];
  offending_carrier_policies: string[];
}

export function PreAuthNewPage(): JSX.Element {
  const params = useParams<{ caseId: string }>();
  const caseId = Number(params.caseId);
  const navigate = useNavigate();

  const clinicalAuthority = useAuthStore((s) => s.clinicalAuthority);
  const providerAllowed =
    clinicalAuthority === "clinic_staff" || clinicalAuthority === "provider";

  const { data: profileData, refresh: refreshProfiles } = useAsync(
    () => insuranceProfiles.list(),
    [],
  );
  const profiles = profileData?.profiles ?? [];

  // Attending-note picker source — the Case's documents (§A7). Only a
  // fully-processed ("complete") document can be designated the attending note,
  // matching the backend guard; others show disabled with their status.
  const { data: docsData, refresh: refreshDocs } = useAsync(
    () => cases.documents(caseId),
    [caseId],
  );
  const caseDocs = docsData?.documents ?? [];

  const [profileId, setProfileId] = useState<string>("");
  const [goal, setGoal] = useState("medication");
  const [workflow, setWorkflow] = useState("request");
  const [authorship, setAuthorship] = useState("patient");
  const [anticipateDenials, setAnticipateDenials] = useState(false);
  // §3.8 Signing Party overrides (optional; backend defaults per authorship mode).
  const [signingParty, setSigningParty] = useState("");
  const [supervisingPhysician, setSupervisingPhysician] = useState("");
  const [treatment, setTreatment] = useState("");
  const [clinicalContext, setClinicalContext] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verifierFailure, setVerifierFailure] = useState<VerifierFailure | null>(null);

  // Inline "add insurance profile" mini-form.
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [pCarrier, setPCarrier] = useState("");
  const [pPlanType, setPPlanType] = useState("");
  const [pState, setPState] = useState("TX");
  const [savingProfile, setSavingProfile] = useState(false);

  // Attending-note state — the designated Case document, plus the inline
  // "quick note" composer (gated to accounts with clinical authority).
  const [attendingNoteId, setAttendingNoteId] = useState<string>("");
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Physician composes a quick attending note inline: store it as a Case
  // document via the existing upload → associate pipeline (so it inherits the
  // Vault + per-tenant privacy handling unchanged), wait for ingestion to finish,
  // then designate it. No new server path — a note is just a text document.
  const onSaveNote = async (): Promise<void> => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([text], `attending-note-${stamp}.txt`, { type: "text/plain" });
      const uploaded = await documents.upload(file);
      await cases.associate(caseId, uploaded.id);

      // Ingestion is async; the note is citable only once "complete". Poll a
      // bounded window, then hand off to the picker either way.
      let status = uploaded.status;
      for (let i = 0; i < 30 && status !== "complete" && status !== "failed"; i += 1) {
        await new Promise((r) => setTimeout(r, 1000));
        const list = await cases.documents(caseId);
        status = list.documents.find((d) => d.document_id === uploaded.id)?.status ?? status;
      }
      await refreshDocs();

      if (status === "failed") {
        pushToast("error", "The note could not be processed. Try again or upload a file.");
      } else if (status === "complete") {
        setAttendingNoteId(String(uploaded.id));
        setShowNoteForm(false);
        setNoteText("");
        pushToast("success", "Attending note saved and selected");
      } else {
        pushToast(
          "info",
          "Note saved — it's still processing. Select it from the list once ready.",
        );
        setShowNoteForm(false);
        setNoteText("");
      }
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Could not save the note");
    } finally {
      setSavingNote(false);
    }
  };

  const onSaveProfile = async (): Promise<void> => {
    if (!pCarrier.trim() || !pPlanType.trim() || !pState.trim()) return;
    setSavingProfile(true);
    try {
      const created = await insuranceProfiles.create({
        carrier: pCarrier.trim(),
        plan_type: pPlanType.trim(),
        state: pState.trim(),
      });
      pushToast(
        "success",
        created.supported_carrier
          ? `Saved ${created.carrier} (supported carrier)`
          : `Saved ${created.carrier} (not a v1 carrier — fallback path)`,
      );
      setShowProfileForm(false);
      setPCarrier("");
      setPPlanType("");
      await refreshProfiles();
      setProfileId(String(created.id));
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!treatment.trim()) return;
    setVerifierFailure(null);
    setSubmitting(true);
    try {
      const req = await preauth.createRequest({
        case_id: caseId,
        insurance_profile_id: profileId ? Number(profileId) : null,
        goal,
        workflow_mode: workflow,
        authorship_mode: authorship,
        anticipate_denials: workflow === "request" ? anticipateDenials : false,
        treatment_name: treatment.trim(),
        clinical_context: clinicalContext.trim() || null,
      });
      const draft = await preauth.createDraft(req.id, {
        signing_party: signingParty.trim() || null,
        supervising_physician:
          authorship === "provider" ? supervisingPhysician.trim() || null : null,
        attending_note_document_id: attendingNoteId ? Number(attendingNoteId) : null,
      });
      pushToast("success", `Preauth letter #${draft.id} ready`);
      navigate(`/preauth-drafts/${draft.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "DRAFT_CITATION_UNVERIFIED" && err.details) {
        const d = err.details;
        setVerifierFailure({
          offending_citations: Array.isArray(d.offending_citations)
            ? (d.offending_citations as string[])
            : [],
          offending_document_ids: Array.isArray(d.offending_document_ids)
            ? (d.offending_document_ids as number[])
            : [],
          offending_carrier_policies: Array.isArray(d.offending_carrier_policies)
            ? (d.offending_carrier_policies as string[])
            : [],
        });
      } else if (err instanceof ApiError && err.code === "NOT_APPLICABLE_JURISDICTION") {
        pushToast(
          "error",
          "The Preauthorization Writer's procedural layer is Texas-only in v1; your tenant is not Texas.",
        );
      } else if (err instanceof ApiError && err.code === "PROVIDER_AUTHORITY_REQUIRED") {
        pushToast(
          "error",
          "Provider authorship requires a provisioned clinical authority. Use Patient or Helper authorship.",
        );
      } else if (err instanceof ApiError && err.code === "ATTENDING_NOTE_NOT_IN_CASE") {
        pushToast(
          "error",
          "The selected attending note isn't a document of this Case. Pick another or refresh.",
        );
        setAttendingNoteId("");
        await refreshDocs();
      } else {
        pushToast("error", err instanceof ApiError ? err.message : "Draft generation failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <nav className="mb-2 text-sm text-slate-500">
        <Link to={`/cases/${caseId}`} className="hover:text-brand-700">
          Case #{caseId}
        </Link>
        <span aria-hidden> / </span>
        <span>New preauthorization letter</span>
      </nav>
      <h1 className="mb-2 text-2xl font-semibold text-slate-900">Preauthorization Writer</h1>
      <p className="mb-3 max-w-prose-narrow text-sm text-slate-600">
        Drafts a prior-authorization request or appeal letter structured around the carrier's
        own policy, your Texas Insurance Code procedural rights, and your medical records. Every
        carrier-policy and statute citation is verified against the ingested corpus.
      </p>

      {/* Supported jurisdictions + carriers (AC: surface these prominently). */}
      <div className="mb-4 max-w-prose-narrow rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <strong className="text-slate-700">v1 coverage:</strong> supported carriers —
        UnitedHealthcare, Aetna · procedural code — Texas Insurance Code. Other carriers still
        get a letter from your plan type, the state code, and your records, but without
        carrier-specific policy criteria.
      </div>

      {verifierFailure && (
        <div className="mb-4 max-w-prose-narrow rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <strong>Citation Verifier rejected the draft.</strong>
          {verifierFailure.offending_citations.length > 0 && (
            <div>
              Unverified citations: <code>{verifierFailure.offending_citations.join(", ")}</code>
            </div>
          )}
          {verifierFailure.offending_carrier_policies.length > 0 && (
            <div>
              Unverified carrier policies:{" "}
              <code>{verifierFailure.offending_carrier_policies.join(", ")}</code>
            </div>
          )}
          {verifierFailure.offending_document_ids.length > 0 && (
            <div>
              Unknown document ids:{" "}
              <code>{verifierFailure.offending_document_ids.join(", ")}</code>
            </div>
          )}
        </div>
      )}

      <form onSubmit={onSubmit} className="card max-w-prose-narrow space-y-4 p-6">
        {/* Insurance profile (AC: select which profile applies). */}
        <div>
          <label htmlFor="profile" className="label">
            Insurance profile
          </label>
          <div className="flex gap-2">
            <select
              id="profile"
              className="input"
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
            >
              <option value="">(none — use plan-type fallback)</option>
              {profiles.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.carrier} · {p.plan_type} · {p.state}
                  {p.supported_carrier ? " ✓" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              onClick={() => setShowProfileForm((v) => !v)}
            >
              {showProfileForm ? "Cancel" : "Add profile"}
            </button>
          </div>
        </div>

        {showProfileForm && (
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input
                className="input"
                placeholder="Carrier (e.g. Aetna)"
                value={pCarrier}
                onChange={(e) => setPCarrier(e.target.value)}
              />
              <input
                className="input"
                placeholder="Plan type (e.g. Commercial PPO)"
                value={pPlanType}
                onChange={(e) => setPPlanType(e.target.value)}
              />
              <input
                className="input"
                placeholder="State"
                value={pState}
                onChange={(e) => setPState(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="btn-primary"
                disabled={savingProfile || !pCarrier.trim() || !pPlanType.trim() || !pState.trim()}
                onClick={onSaveProfile}
              >
                {savingProfile ? "Saving…" : "Save profile"}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="goal" className="label">
              Goal
            </label>
            <select id="goal" className="input" value={goal} onChange={(e) => setGoal(e.target.value)}>
              {GOALS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="workflow" className="label">
              Workflow
            </label>
            <select
              id="workflow"
              className="input"
              value={workflow}
              onChange={(e) => setWorkflow(e.target.value)}
            >
              {WORKFLOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="authorship" className="label">
              Authorship
            </label>
            <select
              id="authorship"
              className="input"
              value={authorship}
              onChange={(e) => setAuthorship(e.target.value)}
            >
              {AUTHORSHIPS.map((a) => (
                <option key={a.value} value={a.value} disabled={a.value === "provider" && !providerAllowed}>
                  {a.label}
                  {a.value === "provider" && !providerAllowed ? " (requires clinical authority)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!providerAllowed && (
          <p className="text-xs text-slate-500">
            Provider authorship is unlocked only for accounts provisioned with clinical authority
            (clinic staff or provider). Patient and Helper authorship are available to everyone.
          </p>
        )}

        {/* §3.8 Signing Party editor — optional overrides; left blank, the letter
            is signed by the default identity for the chosen authorship mode. */}
        <div>
          <label htmlFor="signing-party" className="label">
            Signing party (optional)
          </label>
          <input
            id="signing-party"
            className="input"
            value={signingParty}
            onChange={(e) => setSigningParty(e.target.value)}
            placeholder="Who signs the letter — defaults to the authorship identity"
          />
        </div>

        {/* Supervising physician applies when clinic staff prepare a Provider-mode
            letter the physician signs (§A7 Phase 4). */}
        {authorship === "provider" && (
          <div>
            <label htmlFor="supervising-physician" className="label">
              Supervising physician (optional)
            </label>
            <input
              id="supervising-physician"
              className="input"
              value={supervisingPhysician}
              onChange={(e) => setSupervisingPhysician(e.target.value)}
              placeholder="Named physician of record when clinic staff prepare the letter"
            />
          </div>
        )}

        {/* Anticipate-denials is only meaningful in Request mode (§A2). */}
        {workflow === "request" && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={anticipateDenials}
              onChange={(e) => setAnticipateDenials(e.target.checked)}
            />
            Anticipate likely denials (more defensively structured request)
          </label>
        )}

        {/* Appeal mode requires a denial letter (AC). */}
        {workflow === "appeal" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
            Appeal mode reads the <strong>denial letter</strong> and supporting records associated
            with this Case. Upload the denial letter (and the contradicting medical records) on the
            Case's <Link to={`/cases/${caseId}`} className="underline">Documents tab</Link> before
            generating, so the writer can rebut the carrier's stated reason point-by-point.
          </div>
        )}

        <div>
          <label htmlFor="treatment" className="label">
            Treatment / medication name
          </label>
          <input
            id="treatment"
            className="input"
            required
            value={treatment}
            onChange={(e) => setTreatment(e.target.value)}
            placeholder="e.g. Humira (adalimumab), lumbar fusion, lumbar ESI"
          />
        </div>

        <div>
          <label htmlFor="clinical" className="label">
            Clinical context (optional)
          </label>
          <textarea
            id="clinical"
            rows={4}
            className="input"
            value={clinicalContext}
            onChange={(e) => setClinicalContext(e.target.value)}
            placeholder="Diagnosis, prior treatments tried, relevant findings. The writer also pulls facts from the records on this Case."
          />
        </div>

        {/* Attending note (§A7) — cite an existing Case document as the attending
            note, or, for a clinical-authority account, compose a quick note inline
            (stored as a Case document via the normal upload pipeline). */}
        <div>
          <label htmlFor="attending-note" className="label">
            Attending note (optional)
          </label>
          <div className="flex gap-2">
            <select
              id="attending-note"
              className="input"
              value={attendingNoteId}
              onChange={(e) => setAttendingNoteId(e.target.value)}
            >
              <option value="">(none)</option>
              {caseDocs.map((d) => (
                <option
                  key={d.document_id}
                  value={String(d.document_id)}
                  disabled={d.status !== "complete"}
                >
                  {d.filename}
                  {d.status !== "complete" ? ` (${d.status})` : ""}
                </option>
              ))}
            </select>
            {providerAllowed && (
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={() => setShowNoteForm((v) => !v)}
              >
                {showNoteForm ? "Cancel" : "Write a quick note"}
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Cite an existing Case document as the attending note that anchors medical necessity
            {providerAllowed ? ", or write a quick one inline." : "."}
          </p>
        </div>

        {providerAllowed && showNoteForm && (
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <textarea
              id="attending-note-text"
              rows={4}
              className="input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Brief attending note — diagnosis, exam findings, and the clinical basis for the requested treatment. Saved to this Case as a document and cited as the attending note."
            />
            <div className="flex justify-end">
              <button
                type="button"
                className="btn-primary"
                disabled={savingNote || !noteText.trim()}
                onClick={onSaveNote}
              >
                {savingNote ? "Saving…" : "Save note"}
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-slate-400">
          This tool does not provide medical or legal advice. Final clinical judgment rests with the
          treating provider, and procedural rights vary by plan type (ERISA vs. state-regulated vs.
          Medicare/Medicaid).
        </p>

        <div className="flex justify-end gap-2">
          <Link to={`/cases/${caseId}`} className="btn-secondary">
            Cancel
          </Link>
          <button type="submit" className="btn-primary" disabled={submitting || !treatment.trim()}>
            {submitting ? "Generating…" : "Generate letter"}
          </button>
        </div>
      </form>
    </div>
  );
}
